import React, { useState } from 'react';
import { Receipt, Euro, Plus, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import api from '@/utils/api';

const EXPENSE_TYPES = [
  { value: 'hotel', label: 'Hotel' },
  { value: 'meal', label: 'Meals' },
  { value: 'transport', label: 'Transport' },
  { value: 'phone', label: 'Phone' },
  { value: 'other', label: 'Other' },
];

const CURRENCIES = [
  { value: 'EUR', label: '€ EUR' },
  { value: 'USD', label: '$ USD' },
  { value: 'GBP', label: '£ GBP' },
  { value: 'CHF', label: 'CHF' },
];

const getExpenseTypeLabel = (type) => {
  const labels = {
    hotel: 'Hotel',
    meal: 'Meals',
    transport: 'Transport',
    phone: 'Phone',
    other: 'Other',
  };
  return labels[type] || type || 'Unknown';
};

const getExpenseTypeVariant = (type) => {
  const variants = {
    hotel: 'info',
    meal: 'success',
    transport: 'warning',
    phone: 'secondary',
    other: 'secondary',
  };
  return variants[type] || 'secondary';
};

const formatCurrency = (amountCents, currency = 'EUR') => {
  if (!amountCents) return '-';
  const amount = amountCents / 100;
  return new Intl.NumberFormat('de-DE', {
    style: 'currency',
    currency: currency,
  }).format(amount);
};

const ExpensesSection = ({ caseId, expenses: initialExpenses, onExpenseAdded, canAdd = true }) => {
  const [expenses, setExpenses] = useState(initialExpenses || []);
  const [modalOpen, setModalOpen] = useState(false);
  const [saving, setSaving] = useState(false);

  // Form state
  const [expenseType, setExpenseType] = useState('other');
  const [amount, setAmount] = useState('');
  const [currency, setCurrency] = useState('EUR');
  const [description, setDescription] = useState('');

  // Sync with parent when initialExpenses changes
  React.useEffect(() => {
    setExpenses(initialExpenses || []);
  }, [initialExpenses]);

  // Group totals by currency
  const totalsByCurrency = expenses.reduce((acc, exp) => {
    const curr = exp.currency || 'EUR';
    acc[curr] = (acc[curr] || 0) + (exp.amount_cents || 0);
    return acc;
  }, {});

  const resetForm = () => {
    setExpenseType('other');
    setAmount('');
    setCurrency('EUR');
    setDescription('');
  };

  const handleOpenModal = () => {
    resetForm();
    setModalOpen(true);
  };

  const handleSave = async () => {
    // Validate
    const amountValue = parseFloat(amount);
    if (isNaN(amountValue) || amountValue <= 0) {
      toast.error('Please enter a valid amount');
      return;
    }

    setSaving(true);
    try {
      const response = await api.post(`/api/cases/${caseId}/intake/expenses/`, {
        expense_type: expenseType,
        amount_cents: Math.round(amountValue * 100),
        currency: currency,
        description: description.trim(),
      });

      if (response.data.status === 'success') {
        // Add the new expense to local state
        setExpenses(prev => [...prev, response.data.expense]);
        setModalOpen(false);
        toast.success('Expense added');

        // Notify parent to refresh if needed
        if (onExpenseAdded) {
          onExpenseAdded();
        }
      }
    } catch (err) {
      console.error('Failed to add expense:', err);
      toast.error('Failed to add expense. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <div>
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <Receipt className="h-5 w-5 text-green-500" />
            <h5 className="font-semibold">Expenses</h5>
            <Badge variant="success" className="text-xs">{expenses.length}</Badge>
          </div>
          {canAdd && caseId && (
            <Button variant="outline" size="sm" onClick={handleOpenModal}>
              <Plus className="h-3.5 w-3.5 mr-1" />
              Add
            </Button>
          )}
        </div>

        {expenses.length === 0 ? (
          <p className="text-slate-500 text-sm text-center py-4">No expenses yet</p>
        ) : (
          <div className="space-y-2">
            {expenses.map((expense) => (
              <div
                key={expense.id}
                className="flex items-center gap-3 p-2 bg-slate-50 rounded-md"
              >
                <Badge variant={getExpenseTypeVariant(expense.expense_type)} className="text-xs shrink-0">
                  {getExpenseTypeLabel(expense.expense_type)}
                </Badge>
                <span className="flex-1 text-sm text-slate-600">
                  {expense.description || '—'}
                </span>
                <span className="text-sm font-semibold tabular-nums text-right shrink-0">
                  {formatCurrency(expense.amount_cents, expense.currency)}
                </span>
              </div>
            ))}

            <Separator className="my-2" />

            <div className="space-y-1.5 px-2">
              {Object.entries(totalsByCurrency).map(([currency, total]) => (
                <div key={currency} className="flex items-center justify-between">
                  <div className="flex items-center gap-1.5">
                    <Euro className="h-4 w-4 text-green-500" />
                    <span className="text-sm font-semibold">Total</span>
                  </div>
                  <span className="font-bold text-green-600">{formatCurrency(total, currency)}</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Add Expense Modal */}
      <Dialog open={modalOpen} onOpenChange={setModalOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Add Expense</DialogTitle>
            <DialogDescription>
              Add an expense you incurred due to the flight disruption.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            {/* Expense Type */}
            <div className="space-y-2">
              <Label htmlFor="expense-type">Type</Label>
              <Select value={expenseType} onValueChange={setExpenseType}>
                <SelectTrigger id="expense-type">
                  <SelectValue placeholder="Select type" />
                </SelectTrigger>
                <SelectContent>
                  {EXPENSE_TYPES.map((type) => (
                    <SelectItem key={type.value} value={type.value}>
                      {type.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Amount and Currency */}
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label htmlFor="amount">Amount</Label>
                <Input
                  id="amount"
                  type="number"
                  step="0.01"
                  min="0"
                  placeholder="0.00"
                  value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="currency">Currency</Label>
                <Select value={currency} onValueChange={setCurrency}>
                  <SelectTrigger id="currency">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {CURRENCIES.map((curr) => (
                      <SelectItem key={curr.value} value={curr.value}>
                        {curr.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Description */}
            <div className="space-y-2">
              <Label htmlFor="description">Description (optional)</Label>
              <Input
                id="description"
                placeholder="e.g., Taxi to hotel"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="ghost" onClick={() => setModalOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleSave} disabled={saving || !amount}>
              {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Add Expense
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default ExpensesSection;
